import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Helmet } from 'react-helmet';

import {
  RadioOptionsWithConfirm,
  AddressInputWithConfirm,
  PhoneNumberInputWithConfirm,
  IQuestion,
  CardContainer,
  CardWithModal
} from 'shared-components';

import { ApplicationState } from '../../../../../store';
import * as StudentActions from '../../../../../store/student/actions';
import * as VPFActions from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import {
  createNavigateBackwardAction,
  createNavigateForwardAction,
  createNavigateToAction
} from '../../../../../middleware/actions';
import ChildrenDetails from './childrenDetails';
import { IChildDetails, IPhone } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { toggleTaskMenu } from '../../../../../store/ui/actions';
import { boolToYesNo } from '../../../../../utilities/formatting';
import { isPersonTravellingWith } from '../../../../../utilities/pages';
import { titleAppend } from '../../../../../utilities/head';

const VisaPrepFormFamilyPage5 = () => {
  // dispatch actions
  const dispatch = useDispatch();
  const onPrevious = () => dispatch(createNavigateBackwardAction());
  const onNext = () => {
    dispatch(StudentActions.saveVisaPrepForm(Pages.PAGE3_5));
    dispatch(createNavigateForwardAction());
  };
  const onSkip = () => dispatch(createNavigateForwardAction());
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(VPFActions.createUpdateVPIFieldAction(fieldName, value, refValue));
  const onBackToTaskList = () => {
    dispatch(createNavigateToAction('/dashboard'));
    dispatch(toggleTaskMenu(false));
  };

  // state props from store
  const studentVisaPrepForm = useSelector(({ student }: ApplicationState) => student.visa, shallowEqual);

  // constants
  const currentStep = 5;
  const steps = 5;
  const sectionTitle = 'Family';
  const childrenDetails = useMemo(
    () => ({
      hasDependantChildren: studentVisaPrepForm.hasDependantChildren,
      children: [...(studentVisaPrepForm.children ?? [])],
      childrenLivingWithYou: studentVisaPrepForm.childrenLivingWithYou,
      contactAddress: studentVisaPrepForm.contactAddress,
      contactCity: studentVisaPrepForm.contactCity,
      contactPostcode: studentVisaPrepForm.contactPostcode,
      contactCountry: studentVisaPrepForm.contactCountry,
      contactNumber: {
        code: studentVisaPrepForm.contactNumber ? studentVisaPrepForm.contactNumber.split('-')[0] : null,
        phone: studentVisaPrepForm.contactNumber ? studentVisaPrepForm.contactNumber.split('-')[1] : null
      }
    }),
    [studentVisaPrepForm]
  );

  // component state
  const [complete, setComplete] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<IPhone>();
  const [isPhoneNumberValid, setPhoneNumberValid] = useState(true);
  const [childrenState, setChildrenState] = useState<IChildDetails[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setChildrenState([
      ...childrenDetails.children,
      {
        nationality: '',
        gender: '',
        familyName: '',
        givenName: '',
        dob: '',
        sameNationality: undefined,
        ukVisa: undefined,
        hasPassport: undefined,
        passportNumber: '',
        placeOfBirth: '',
        travellingWith: ''
      }
    ]);
    setShowModal([...childrenDetails.children.map(_ => false), false]);
  }, [childrenDetails.children]);

  useEffect(() => {
    setPhoneNumber(childrenDetails.contactNumber);
  }, [childrenDetails.contactNumber]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      ...childrenDetails,
      isPhoneNumberValid
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, childrenDetails, isPhoneNumberValid]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteChild = (index: number): void => {
    onChange(VQ.CHILD_DETAILS, [
      ...childrenDetails.children.slice(0, index),
      ...childrenDetails.children.slice(index + 1)
    ]);
  };

  const onYesNoQuestionButtonChange = (key: VQ, question: IQuestion) => {
    onChange(key, question.label === 'Yes');
  };

  const getCardItems = (details: IChildDetails) => {
    let cardItems = [
      {
        label: 'Nationality',
        value: details.nationality
      },
      {
        label: 'Gender',
        value: details.gender
      },
      {
        label: 'Always had the same nationality?',
        value: boolToYesNo(details.sameNationality)
      },
      {
        label: 'Family name',
        value: details.familyName
      },
      {
        label: 'Given name',
        value: details.givenName
      },
      {
        label: 'Date of birth',
        value: details.dob
      },
      {
        label: 'Place of birth',
        value: details.placeOfBirth
      },
      {
        label: 'Is this person travelling with you?',
        value: details.travellingWith
      }
    ];
    if (isPersonTravellingWith(details.travellingWith)) {
      cardItems = cardItems.concat([
        {
          label: 'Visa for the UK?',
          value: boolToYesNo(details.ukVisa)
        },
        {
          label: 'Passport or Travel Document?',
          value: boolToYesNo(details.hasPassport)
        }
      ]);
      if (details.hasPassport === true) {
        cardItems = cardItems.concat([
          {
            label: 'Document Number',
            value: details.passportNumber
          }
        ]);
      }
    }
    return cardItems;
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onPrevious={onPrevious}
      onNext={onNext}
      onSkip={onSkip}
      onBackToTaskList={onBackToTaskList}
      section={sectionTitle}
    >
      <Helmet>
        <title>
          {`${sectionTitle}: Step ${currentStep}`} {titleAppend}
        </title>
      </Helmet>

      <RadioOptionsWithConfirm
        key={VQ.HAS_DEPENDANT_CHILDREN}
        id={VQ.HAS_DEPENDANT_CHILDREN}
        label={`Do you have any dependant children?`}
        onClick={(question: IQuestion) => onYesNoQuestionButtonChange(VQ.HAS_DEPENDANT_CHILDREN, question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: childrenDetails.hasDependantChildren === true
          },
          {
            char: 'N',
            label: 'No',
            selected: childrenDetails.hasDependantChildren === false
          }
        ]}
      />

      {childrenDetails.hasDependantChildren === true ? (
        <>
          <CardContainer title={`How many children?`}>
            {childrenState.map((details: IChildDetails, index: number) => (
              <CardWithModal
                key={index}
                id={`children-${index}`}
                index={index}
                modalTitle={'Add a Child'}
                cardItems={getCardItems(details)}
                onRemove={deleteChild}
                addButtonLabel={`Add a Child`}
                addAnotherButtonLabel={`I have another Child to add`}
                hasAddMoreButton={
                  details.nationality === '' &&
                  details.gender === '' &&
                  details.familyName === '' &&
                  details.givenName === '' &&
                  details.dob === '' &&
                  details.sameNationality === undefined &&
                  details.ukVisa === undefined &&
                  details.hasPassport === undefined &&
                  details.passportNumber === '' &&
                  details.placeOfBirth === '' &&
                  details.travellingWith === ''
                }
                showModal={showModal[index]}
                toggleModal={toggleModal}
              >
                <ChildrenDetails
                  index={index}
                  nationality={details.nationality}
                  gender={details.gender}
                  familyName={details.familyName}
                  givenName={details.givenName}
                  dob={details.dob}
                  sameNationality={details.sameNationality}
                  ukVisa={details.ukVisa}
                  hasPassport={details.hasPassport}
                  passportNumber={details.passportNumber}
                  placeOfBirth={details.placeOfBirth}
                  travellingWith={details.travellingWith}
                  onSaveAndClose={(details: IChildDetails) => {
                    const childrenNew = [...childrenDetails.children];
                    childrenNew[index] = {
                      ...details
                    };
                    onChange(VQ.CHILD_DETAILS, childrenNew);
                    toggleModal(index);
                  }}
                />
              </CardWithModal>
            ))}
          </CardContainer>

          <RadioOptionsWithConfirm
            key={VQ.CHILDREN_LIVING_WITH_YOU}
            id={VQ.CHILDREN_LIVING_WITH_YOU}
            label={`Are these children living with you currently?`}
            onClick={(question: IQuestion) => onChange(VQ.CHILDREN_LIVING_WITH_YOU, question.label === 'Yes')}
            isInline={true}
            questions={[
              {
                char: 'Y',
                label: 'Yes',
                selected: childrenDetails.childrenLivingWithYou === true
              },
              {
                char: 'N',
                label: 'No',
                selected: childrenDetails.childrenLivingWithYou === false
              }
            ]}
          />

          {childrenDetails.childrenLivingWithYou === false ? (
            <>
              <AddressInputWithConfirm
                key={VQ.CONTACT_ADDRESS}
                id={VQ.CONTACT_ADDRESS}
                addressLabel="Contact Address"
                addressSubText="(e.g. unit 33, 17 Example Street)"
                cityLabel="City"
                postcodeLabel="Postcode (if you have one)"
                countryLabel="Country"
                addressValue={childrenDetails.contactAddress}
                cityValue={childrenDetails.contactCity}
                postcodeValue={childrenDetails.contactPostcode}
                countryValue={childrenDetails.contactCountry}
                onChangeAddress={(event: any) => {
                  onChange(VQ.CONTACT_ADDRESS, event.currentTarget.value);
                }}
                onChangeCity={(event: any) => {
                  onChange(VQ.CONTACT_CITY, event.currentTarget.value);
                }}
                onChangePostcode={(event: any) => {
                  onChange(VQ.CONTACT_POSTCODE, event.currentTarget.value);
                }}
                onChangeCountry={(value: string) => {
                  onChange(VQ.CONTACT_COUNTRY, value);
                }}
              />

              <PhoneNumberInputWithConfirm
                key={VQ.CONTACT_NUMBER}
                id={VQ.CONTACT_NUMBER}
                value={phoneNumber ? phoneNumber : ''}
                label={'Contact number'}
                codeValue={phoneNumber && phoneNumber.code ? phoneNumber.code : ''}
                numberValue={phoneNumber && phoneNumber.phone ? phoneNumber.phone : ''}
                onValidationFailed={() => {
                  setPhoneNumberValid(false);
                }}
                onValidationSuccess={() => {
                  const { code, phone } = phoneNumber!;
                  setPhoneNumberValid(true);
                  onChange(VQ.CONTACT_NUMBER, `${code}-${phone}`);
                }}
                onChangeCode={(event: React.FormEvent<HTMLInputElement>) => {
                  const code = event.currentTarget.value;
                  setPhoneNumber({
                    phone: phoneNumber ? phoneNumber.phone : '',
                    code
                  });
                }}
                onChangeNumber={(event: React.FormEvent<HTMLInputElement>) => {
                  const value = event.currentTarget.value;
                  setPhoneNumber({
                    phone: value,
                    code: phoneNumber ? phoneNumber.code : ''
                  });
                }}
              />
            </>
          ) : null}
        </>
      ) : null}
    </QuestionPage>
  );
};
export default VisaPrepFormFamilyPage5;
